import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Send, Sparkles, Users, Calendar, MapPin, CheckCircle } from 'lucide-react';

const MiniAIAssistantPreview = ({ isActive = true }) => {
  const [currentMessage, setCurrentMessage] = useState(-1);
  const [isTyping, setIsTyping] = useState(false);
  const [userTyping, setUserTyping] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [animationCycle, setAnimationCycle] = useState(0); // Track animation cycles for key prop
  const chatContainerRef = useRef(null);

  // Reset chat when component becomes inactive
  useEffect(() => {
    if (!isActive) {
      // Complete reset - clear everything
      setCurrentMessage(-1);
      setUserTyping('');
      setIsUserTyping(false);
      setIsTyping(false);
      // Force scroll reset
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [isActive]);

  const conversation = [
    {
      type: 'ai',
      message: 'Hi Dave! How can I assist you today?'
    },
    {
      type: 'user',
      message: 'What training does my team need to be compliant?'
    },
    {
      type: 'ai',
      message: 'I\'ve analysed your compliance requirements. Here\'s what needs attention:',
      recommendations: [
        {
          name: 'Fire Safety Training',
          status: 'Critical - Required by law',
          valid: 8,
          total: 24,
          color: 'red'
        },
        {
          name: 'First Aid Certification',
          status: 'Expiring Soon',
          valid: 18,
          total: 24,
          color: 'amber'
        },
        {
          name: 'GDPR Compliance',
          status: 'Good Standing',
          valid: 22,
          total: 24,
          color: 'green'
        }
      ]
    },
    {
      type: 'user',
      message: 'Book fire safety'
    },
    {
      type: 'ai',
      message: '✅ Successfully created quote',
      quoteDetails: {
        title: 'Fire Safety Training',
        courseProvider: 'SafeGuard Training Ltd',
        items: [
          { icon: Calendar, label: 'Date', value: 'Nov 15, 2025' },
          { icon: Users, label: 'Delegates', value: '16 members' },
          { icon: MapPin, label: 'Location', value: 'On-site' },
          { icon: CheckCircle, label: 'Total', value: '£1,920' }
        ]
      }
    },
    {
      type: 'ai',
      message: 'Based on your preferences, I\'ve created this quote. Do you require any changes?'
    },
    {
      type: 'user',
      message: 'Please change the date to December 15th 2025'
    },
    {
      type: 'ai',
      message: '✅ Quote updated successfully',
      quoteDetails: {
        title: 'Fire Safety Training',
        courseProvider: 'SafeGuard Training Ltd',
        items: [
          { icon: Calendar, label: 'Date', value: 'Dec 15, 2025' },
          { icon: Users, label: 'Delegates', value: '16 members' },
          { icon: MapPin, label: 'Location', value: 'On-site' },
          { icon: CheckCircle, label: 'Total', value: '£1,920' }
        ],
        updated: true
      }
    },
    {
      type: 'user',
      message: 'Thanks!'
    },
    {
      type: 'ai',
      message: 'Always here to help! Just ask if you need anything else with your training and compliance requirements.'
    }
  ];

  useEffect(() => {
    if (!isActive) return;

    let timeoutId;
    let typingTimeouts = [];

    // Simple timing logic
    const timings = [
      2000,  // AI greeting - wait 2s
      2500,  // User types question + send - wait 2.5s
      4000,  // AI recommendations with progress bars - wait 4s to read
      2000,  // User types booking + send - wait 2s
      100,   // Quote details - immediately show follow-up
      3000,  // AI asks if changes needed - wait 3s to read both messages
      2500,  // User types date change + send - wait 2.5s
      100,   // Updated quote details - immediately show
      3000,  // Wait to read the updated quote
      1500,  // User types thanks - wait 1.5s
      10000  // AI final message - wait 10 seconds before restart
    ];

    // Handle next message
    const handleNextMessage = () => {
      const nextIndex = currentMessage + 1;

      if (nextIndex < conversation.length) {
        const nextMsg = conversation[nextIndex];

        if (nextMsg.type === 'user') {
          // Start typing animation for user message
          setIsUserTyping(true);
          const message = nextMsg.message;
          let charIndex = 0;

          // Type each character
          const typeChar = () => {
            if (charIndex < message.length) {
              setUserTyping(message.slice(0, charIndex + 1));
              charIndex++;
              typingTimeouts.push(setTimeout(typeChar, 50 + Math.random() * 30));
            } else {
              // Finished typing, wait a bit then send
              typingTimeouts.push(setTimeout(() => {
                setCurrentMessage(nextIndex);
                setUserTyping('');
                setIsUserTyping(false);
              }, 300));
            }
          };

          // Start typing after a short delay
          typingTimeouts.push(setTimeout(typeChar, 500));
        } else {
          // AI message - check if we should show typing or instant
          const showInstantly = currentMessage >= 0 &&
            conversation[currentMessage].type === 'ai' &&
            conversation[currentMessage].quoteDetails;

          if (!showInstantly && currentMessage >= 0 && conversation[currentMessage].type === 'user') {
            setTimeout(() => setIsTyping(true), 600);
          }

          timeoutId = setTimeout(() => {
            setCurrentMessage(nextIndex);
            setIsTyping(false);
          }, showInstantly ? 100 : timings[currentMessage]);
        }
      } else {
        // At the end, wait 10 seconds then restart
        timeoutId = setTimeout(() => {
          // Complete reset for clean restart
          setCurrentMessage(-1);
          setUserTyping('');
          setIsUserTyping(false);
          setIsTyping(false);
          setAnimationCycle(prev => prev + 1); // Increment to force remount

          // Force DOM update and scroll reset
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = 0;
            }
          }, 50);
        }, 10000);
      }
    };

    if (currentMessage === -1) {
      // Start animation with first message
      handleNextMessage();
    } else if (currentMessage >= 0 && conversation[currentMessage].type === 'user') {
      // Just sent a user message, move to AI response
      const nextIndex = currentMessage + 1;
      if (nextIndex < conversation.length && conversation[nextIndex].type === 'ai') {
        setTimeout(() => setIsTyping(true), 600);
        timeoutId = setTimeout(() => {
          setCurrentMessage(nextIndex);
          setIsTyping(false);
        }, 1500);
      }
    } else {
      // Current is AI message, prepare for next
      timeoutId = setTimeout(() => {
        handleNextMessage();
      }, timings[currentMessage]);
    }

    return () => {
      clearTimeout(timeoutId);
      typingTimeouts.forEach(t => clearTimeout(t));
    };
  }, [currentMessage, isActive]);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (chatContainerRef.current) {
      // Use multiple timeouts to ensure scrolling happens after content renders
      const timeouts = [50, 150, 300, 500];
      timeouts.forEach(delay => {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, delay);
      });
    }
  }, [currentMessage, isTyping, userTyping]);

  return (
    <div className="w-full h-full px-8 py-6">
      <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-3 h-full flex flex-col max-w-md mx-auto">
        {/* Header */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">AI Compliance Assistant</h3>
              <p className="text-xs text-gray-500">Intelligent training recommendations</p>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden min-h-0" key={animationCycle}>
          <div className="space-y-2 pr-1">
            <AnimatePresence mode="popLayout">
              {currentMessage >= 0 && conversation.slice(0, currentMessage + 1).map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] ${msg.type === 'user' ? 'order-2' : 'order-1'}`}>
                    {/* Message bubble - always show for all messages */}
                    {msg.type === 'user' ? (
                      <div className="rounded-2xl px-3 py-2 inline-block bg-gray-900 text-white">
                        <p className="text-xs font-medium">{msg.message}</p>
                      </div>
                    ) : (
                      <div className="rounded-2xl px-3 py-2 inline-block bg-white text-gray-900 border border-gray-200 shadow-sm">
                        <p className="text-xs font-medium">{msg.message}</p>
                      </div>
                    )}

                    {/* AI Recommendations with Progress */}
                    {msg.recommendations && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ delay: 0.3 }}
                        onAnimationComplete={() => {
                          if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                          }
                        }}
                        className="mt-2 space-y-1"
                      >
                        {msg.recommendations.map((rec, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + i * 0.15 }}
                            className="bg-white border border-gray-200 rounded-lg p-1.5"
                          >
                            <div className="flex items-center justify-between mb-0.5">
                              <p className="text-xs font-semibold text-gray-900 truncate flex-1 mr-2">{rec.name}</p>
                              <span className={`text-xs font-medium ${
                                rec.color === 'red' ? 'text-red-600' :
                                rec.color === 'amber' ? 'text-amber-600' :
                                'text-green-600'
                              }`}>
                                {rec.valid}/{rec.total}
                              </span>
                            </div>
                            <div className="mb-0.5">
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${(rec.valid / rec.total) * 100}%` }}
                                  transition={{ delay: 0.5 + i * 0.15, duration: 0.5 }}
                                  className={`h-full ${
                                    rec.color === 'red' ? 'bg-red-500' :
                                    rec.color === 'amber' ? 'bg-amber-500' :
                                    'bg-green-500'
                                  }`}
                                />
                              </div>
                            </div>
                            <p className={`text-xs truncate ${
                              rec.color === 'red' ? 'text-red-600' :
                              rec.color === 'amber' ? 'text-amber-600' :
                              'text-green-600'
                            }`}>
                              {rec.status}
                            </p>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}

                    {/* Quote Details - Shows instantly after message */}
                    {msg.quoteDetails && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                        onAnimationComplete={() => {
                          if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                          }
                        }}
                        className="mt-2 bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-xs font-bold text-gray-900">{msg.quoteDetails.title}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{msg.quoteDetails.courseProvider}</p>
                          </div>
                          <span className={`px-2 py-0.5 ${msg.quoteDetails.updated ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'} text-xs font-medium rounded-full`}>
                            {msg.quoteDetails.updated ? 'Updated' : 'Quote'}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {msg.quoteDetails.items.map((item, i) => {
                            const Icon = item.icon;
                            return (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 + i * 0.1 }}
                                className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0"
                              >
                                <Icon className="w-3 h-3 text-gray-400" />
                                <div className="flex-1 flex items-center justify-between">
                                  <span className="text-xs text-gray-600">{item.label}</span>
                                  <span className="text-xs font-medium text-gray-900">{item.value}</span>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 }}
                          className="mt-2 pt-2 border-t border-gray-100"
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                              {msg.quoteDetails.updated ? 'Updated • Valid 30 days' : 'Valid for 30 days'}
                            </p>
                            <p className="text-xs text-gray-400 font-mono">
                              {msg.quoteDetails.updated ? '#QT-251215-R1' : '#QT-251115'}
                            </p>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Typing Indicator */}
            {isTyping && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <div className="bg-gray-100 rounded-2xl px-3 py-2">
                  <div className="flex gap-1">
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0 }}
                      className="w-2 h-2 bg-gray-400 rounded-full"
                    />
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                      className="w-2 h-2 bg-gray-400 rounded-full"
                    />
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                      className="w-2 h-2 bg-gray-400 rounded-full"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Input Preview */}
        <div className="mt-3 flex items-center gap-2 p-1.5 bg-gray-50 rounded-xl flex-shrink-0">
          <input
            type="text"
            value={userTyping}
            placeholder={!isUserTyping ? "Ask about compliance, training, or certifications..." : ""}
            className="flex-1 bg-transparent text-xs px-2 py-0.5 outline-none text-gray-900"
            disabled
            readOnly
          />
          <motion.button
            animate={{
              scale: isUserTyping && userTyping.length > 0 ? [1, 1.1, 1] : 1,
              backgroundColor: isUserTyping && userTyping.length > 0 ? '#1f2937' : '#1f2937'
            }}
            transition={{ duration: 0.2 }}
            className="p-1 bg-gray-900 text-white rounded-lg"
          >
            <Send className="w-3 h-3" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default MiniAIAssistantPreview;